import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomLightBox from "./CustomLightBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import useDebounce from "../../hooks/useDebounce";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para cada imagen sorteable
const SortableImageItem = ({
  item,
  index,
  handleSelect,
  handleSelectImg,
  handleDelete,
  selected,
  disabled,
  disableDeleteOne,
  noDelete,
  btnClassName,
  variant,
  sortable = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.pk || item.id || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: sortable ? 'grab' : 'pointer',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        "Imatges-imgWrapper",
        !!selected[index] && "Imatges-imgWrapper-selected",
        isDragging && "Imatges-imgWrapper-dragging"
      )}
      {...(sortable ? attributes : {})}
    >
      <a
        onClick={handleSelect(item, index)}
        onDoubleClick={handleSelectImg(index)}
        href="#img-preview"
        className={`Imatges-image ${btnClassName}`}
        role="button"
      >
        <img
          className={clsx("Imatges-image", variant)}
          alt={item.alt}
          src={item.thumb || item.src}
        />
      </a>
      {sortable && !disabled && (
        <div 
          className="Imatges-dragHandle" 
          {...listeners}
          style={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '4px',
            padding: '4px 8px',
            cursor: 'grab',
            zIndex: 10,
          }}
        >
          <FontAwesomeIcon icon={["fas", "grip-vertical"]} style={{ color: 'white', fontSize: '14px' }} />
        </div>
      )}
      {!noDelete && !disabled && !disableDeleteOne && (
        <button
          type="button"
          className="Imatges-deleteBtn"
          onClick={handleDelete(item)}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={["fas", "trash"]} />
        </button>
      )}
      {item.title && (
        <div className={`gallery-img-title`}>{item.title}</div>
      )}
    </div>
  );
};

const ImgsWithLightBox = (props) => {
  const {
    onClick,
    isOne,
    btnClassName,
    src = "",
    alt = "",
    images: imagesPreview,
    manual,
    onDelete,
    disabled,
    noDelete,
    variant = "",
    options,
    multiselect = false,
    onMultiselect = () => {},
    sortable = false,
    onReorder = () => {},
    ...otherProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const [viewMore, setViewMore] = useState(false);
  const [selected, setSelected] = useState({});
  const [disableDeleteOne, setDisableDeleteOne] = useState(false);
  const [images, setImages] = useState(imagesPreview || []);

  const updateParent = useDebounce(selected, 500);

  // Configurar sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requiere mover 8px antes de activar el drag (evita conflictos con clicks)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (multiselect) {
      const validSelects = Object.values(updateParent).filter((item) => !!item);
      onMultiselect(validSelects);
      setDisableDeleteOne(!!validSelects.length);
    }
  }, [updateParent]);

  useEffect(() => {
    setSelected({});
    setImages(imagesPreview || []);
  }, [imagesPreview]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => (item.pk || item.id || items.indexOf(item)) === active.id);
        const newIndex = items.findIndex((item) => (item.pk || item.id || items.indexOf(item)) === over.id);
        
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Llamar al callback con el nuevo orden
        // Crear un array con ID y posición de cada imagen
        const reorderedData = newOrder.map((img, index) => ({
          id: img.pk || img.id,
          position: index + 1
        }));
        
        onReorder(reorderedData, newOrder);
        
        return newOrder;
      });
    }
  };

  const toggle = () => setIsOpen((prev) => !prev);
  const toggleViewMore = () => setViewMore((prev) => !prev);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick && typeof onClick === "function") onClick(e);
    toggle();
  };

  const handleDelete = (data) => () => {
    if (onDelete && !disabled) onDelete(data);
  };

  const handleSelect = (data, i) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!multiselect) return handleSelectImg(i)(e);
    if (!disabled) {
      setSelected((prev) => ({
        ...prev,
        [i]: prev[i] ? null : data,
      }));
    }
  };

  const handleSelectImg = (imgIdx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick && typeof onClick === "function") onClick(e);
    setCurrentImg(imgIdx);
    toggle();
  };

  const renderImages = () => {
    if (isOne) {
      return (
        <div className="Imatges-imgWrapper">
          <a
            onClick={handleClick}
            href="#img-preview"
            className={btnClassName}
            role="button"
          >
            <img
              {...otherProps}
              className={clsx("Imatges-image", variant)}
              alt={alt}
              src={src}
            />
          </a>
          {!noDelete && (
            <button
              type="button"
              className="Imatges-deleteBtn"
              onClick={handleDelete()}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={["fas", "trash"]} />
            </button>
          )}
        </div>
      );
    }

    if (!Array.isArray(images) || !images.length) return null;

    // Si sortable está activado, usar DndContext
    if (sortable && !disabled) {
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((item, i) => item.pk || item.id || i)}
            strategy={rectSortingStrategy}
          >
            {images.map((item, i) => (
              <SortableImageItem
                key={item.pk || item.id || `ImgsWithLightBox-${i}-thumb`}
                item={item}
                index={i}
                handleSelect={handleSelect}
                handleSelectImg={handleSelectImg}
                handleDelete={handleDelete}
                selected={selected}
                disabled={disabled}
                disableDeleteOne={disableDeleteOne}
                noDelete={noDelete}
                btnClassName={btnClassName}
                variant={variant}
                sortable={sortable}
              />
            ))}
          </SortableContext>
        </DndContext>
      );
    }

    // Renderizado normal sin drag-and-drop
    return images.map((item, i) => (
      <div
        key={`ImgsWithLightBox-${i}-thumb`}
        className={clsx(
          "Imatges-imgWrapper",
          options?.max_images < i + 1 && !viewMore && "d-none",
          !!selected[i] && "Imatges-imgWrapper-selected",
        )}
      >
        <a
          onClick={handleSelect(item, i)}
          onDoubleClick={handleSelectImg(i)}
          href="#img-preview"
          className={`Imatges-image ${btnClassName}`}
          role="button"
        >
          <img
            className={clsx("Imatges-image", variant)}
            alt={item.alt}
            src={item.thumb || item.src}
          />
        </a>
        {!noDelete && !disabled && !disableDeleteOne && (
          <button
            type="button"
            className="Imatges-deleteBtn"
            onClick={handleDelete(item)}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={["fas", "trash"]} />
          </button>
        )}
        {item.title && (
          <div className={`gallery-img-title`}>{item.title}</div>
        )}
      </div>
    ));
  };

  return (
    <Fragment>
      <div className="Imatges-imagesContainer">
        {renderImages()}
        {imagesPreview && options?.max_images < imagesPreview.length && (
          <button
            type="button"
            className="btn btn-secondary Imatges-loadmore Imatges-image"
            onClick={toggleViewMore}
          >
            {!viewMore ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="icon icon-tabler icons-tabler-outline icon-tabler-eye"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="icon icon-tabler icons-tabler-outline icon-tabler-eye-closed"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M21 9c-2.4 2.667 -5.4 4 -9 4c-3.6 0 -6.6 -1.333 -9 -4" />
                <path d="M3 15l2.5 -3.8" />
                <path d="M21 14.976l-2.492 -3.776" />
                <path d="M9 17l.5 -4" />
                <path d="M15 17l-.5 -4" />
              </svg>
            )}
          </button>
        )}
      </div>
      <CustomLightBox
        toggleLig={toggle}
        isOpen={isOpen}
        images={!isOne ? images : [props, props]}
        manual={!isOne}
        activeIndex={currentImg}
      />
    </Fragment>
  );
};

ImgsWithLightBox.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  alt: PropTypes.string,
  src: PropTypes.string,
  title: PropTypes.string,
  variant: PropTypes.string,
  isOne: PropTypes.bool,
  disabled: PropTypes.bool,
  noDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      style: PropTypes.shape,
      className: PropTypes.string,
      alt: PropTypes.string,
      src: PropTypes.string,
    }),
  ),
};

export default ImgsWithLightBox;
